import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatINR, formatDate } from "@/lib/api";
import { STATUSES, STATUS_COLORS, slug } from "@/lib/constants";
import { useRates } from "@/context/RatesContext";
import { Navbar } from "@/components/Navbar";
import { StatusTimeline } from "@/components/StatusTimeline";
import { DocumentViewer } from "@/components/DocumentViewer";
import { VisitScheduler } from "@/components/VisitScheduler";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Eye, CheckCircle2, XCircle, Loader2, Landmark, Phone, Mail, MapPin } from "lucide-react";

const Field = ({ label, value, testId }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className="mt-1 text-sm font-medium text-[#0F172A]" data-testid={testId}>{value || "—"}</p>
  </div>
);

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const { rates: RATES } = useRates();
  const [app, setApp] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [visit, setVisit] = useState("");
  const [slot, setSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [rate, setRate] = useState("");
  const [lender, setLender] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/applications/${id}`);
      setApp(data); setVisit(data.vip_visit_date || ""); setSlot(data.vip_visit_slot || null); setNotes(data.admin_notes || ""); setRate(String(data.interest_rate ?? RATES[data.loan_type])); setLender(data.lender_name || "");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/applications/${id}/status`, { status });
      setApp({ ...app, ...data });
      toast.success(`Status updated to ${status} · webhook fired`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const verify = async (docId, verification_status) => {
    try {
      await api.patch(`/documents/${docId}/verify`, { verification_status });
      setApp({ ...app, documents: app.documents.map((d) => (d.id === docId ? { ...d, verification_status } : d)) });
      toast.success(`Document marked ${verification_status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAdmin = async () => {
    if (visit && !slot) { toast.error("Pick a time slot for the doorstep visit"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/applications/${id}`, { vip_visit_date: visit || "", vip_visit_slot: slot, admin_notes: notes, interest_rate: Number(rate) || null, lender_name: lender.trim() || null });
      setApp({ ...app, ...data });
      toast.success(visit ? `Doorstep visit scheduled for ${formatDate(visit)} at ${slot}` : "Lender, rate & notes saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  if (!app) return <div className="min-h-screen bg-[#0B132B] flex items-center justify-center"><Loader2 className="h-8 w-8 text-[#FF6B00] animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0B132B] text-white">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-12 py-8 lg:py-12" data-testid="admin-application-detail">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-to-pipeline-link"><ArrowLeft className="h-4 w-4" /> Back to pipeline</Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">{app.loan_type} · Ref {app.id.replace("app_", "").toUpperCase()}</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold" data-testid="detail-client-name">{app.client?.name}</h1>
            <p className="mt-2 text-slate-400 flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {app.client?.phone_number || "—"}</span>
              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {app.client?.email}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="text-white border-0 px-3 py-1" style={{ background: STATUS_COLORS[app.status] }} data-testid="detail-status-badge">{app.status}</Badge>
            <Select value={app.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-56 bg-white text-[#0F172A]" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${slug(s)}`}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6 lg:gap-8">
          <section className="lg:col-span-2 space-y-6">
            <div className="bg-white text-[#0F172A] rounded-2xl p-6 sm:p-8 shadow-xl">
              <StatusTimeline status={app.status} />
              <div className="mt-8 grid sm:grid-cols-5 gap-6">
                <Field label="Requested" value={formatINR(app.requested_amount)} testId="detail-amount" />
                <Field label="Monthly income" value={formatINR(app.monthly_income)} testId="detail-income" />
                <Field label="Tenure" value={app.tenure_years ? `${app.tenure_years} years` : null} testId="detail-tenure" />
                <Field label="Interest rate" value={`${(app.interest_rate ?? RATES[app.loan_type]).toFixed(2)}% p.a.`} testId="detail-rate" />
                <Field label="Partner lender" value={app.lender_name} testId="detail-lender" />
              </div>
              <div className="mt-6 flex items-start gap-2 text-sm text-slate-700"><MapPin className="h-4 w-4 mt-0.5 text-[#FF6B00] shrink-0" /><span data-testid="detail-address">{app.property_address || "No property (business loan)"}</span></div>
            </div>

            <div className="bg-white text-[#0F172A] rounded-2xl p-6 sm:p-8 shadow-xl" data-testid="detail-documents">
              <h2 className="font-display text-base md:text-lg font-semibold">Document dossier ({app.documents.length})</h2>
              {app.documents.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">Client has not uploaded documents yet.</p>
              ) : (
                <ul className="mt-4 divide-y divide-slate-100">
                  {app.documents.map((d) => (
                    <li key={d.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`detail-document-${d.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">{d.document_type} <span className={`ml-2 text-xs font-medium ${d.verification_status === "Verified" ? "text-emerald-600" : d.verification_status === "Rejected" ? "text-red-500" : "text-amber-600"}`} data-testid={`detail-document-status-${d.id}`}>{d.verification_status}</span></p>
                        <p className="text-xs text-slate-500 truncate">{d.original_filename} · {(d.size / 1024).toFixed(0)} KB · {formatDate(d.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => setViewing(d)} data-testid={`view-document-${d.id}`}><Eye className="h-4 w-4 mr-1" /> View</Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => verify(d.id, "Verified")} data-testid={`verify-document-${d.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> Verify</Button>
                        <Button size="sm" variant="destructive" onClick={() => verify(d.id, "Rejected")} data-testid={`reject-document-${d.id}`}><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <aside className="space-y-6">
            <div className="bg-white text-[#0F172A] rounded-2xl p-6 shadow-xl">
              <h2 className="font-display text-base md:text-lg font-semibold flex items-center gap-2"><Landmark className="h-5 w-5 text-[#FF6B00]" /> Partner lender &amp; visit</h2>
              <Label htmlFor="lender" className="mt-4 block">Partner bank / NBFC</Label>
              <Input id="lender" value={lender} onChange={(e) => setLender(e.target.value)} placeholder="e.g. HDFC Bank, ICICI Bank, Bajaj Finserv" className="mt-1.5 h-11" data-testid="lender-name-input" />
              <Label className="mt-5 block">Doorstep visit</Label>
              <div className="mt-1.5"><VisitScheduler appId={id} date={visit} slot={slot} onChange={(d, s) => { setVisit(d); setSlot(s); }} /></div>
              {app.vip_visit_date && <p className="mt-2 text-xs text-slate-500" data-testid="vip-visit-current">Currently scheduled: {formatDate(app.vip_visit_date)}{app.vip_visit_slot ? ` at ${app.vip_visit_slot}` : ""}</p>}
              <Label htmlFor="rate" className="mt-5 block">Lender's sanctioned rate (% p.a.)</Label>
              <Input id="rate" type="number" min="5" max="24" step="0.05" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1.5 h-11" data-testid="admin-interest-rate-input" />
              <p className="mt-1 text-xs text-slate-500">Indicative rate card: {RATES[app.loan_type].toFixed(2)}% · printed on the sanction communication</p>
              <Label htmlFor="notes" className="mt-5 block">Legal review notes</Label>
              <Textarea id="notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Title chain verified till 1998…" className="mt-1.5" data-testid="admin-notes-input" />
              <Button onClick={saveAdmin} disabled={saving} className="gold-btn w-full mt-5 h-11 rounded-full" data-testid="save-admin-updates-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save lender, visit, rate & notes"}
              </Button>
            </div>
            <div className="navy-glass rounded-2xl p-6">
              <h2 className="font-display text-base md:text-lg font-semibold">Status history</h2>
              <ul className="mt-3 space-y-2 text-sm" data-testid="status-history">
                {[...app.status_history].reverse().map((h, i) => (
                  <li key={i} className="flex justify-between gap-2 text-slate-300"><span>{h.status}</span><span className="text-slate-500">{formatDate(h.at)}</span></li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </main>
      <DocumentViewer doc={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
